import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useDndContext, useDroppable } from '@dnd-kit/core';
import { Box } from '@mui/material';
import { DndColumn, DndParentEntry } from '../types';
import { useSelectionContext, SelectionProvider } from '../contexts/SelectionContext';
import DndItemCard from './DndItemCard';
import DndChildCard from './DndChildCard';

// ─── Virtualisation par IntersectionObserver ──────────────────────────────────

const useIsVisible = (
  ref: React.RefObject<HTMLElement | null>,
  rootRef: React.RefObject<HTMLElement | null>
) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), {
      root: rootRef.current,
      rootMargin: '100px',
      threshold: 0
    });
    const current = ref.current;
    if (current) observer.observe(current);
    return () => {
      if (current) observer.unobserve(current);
    };
  }, [ref, rootRef]);

  return isVisible;
};

export const VisibleDndCard = ({
  item,
  columnId,
  isOpen,
  onToggleAccordion,
  scrollContainerRef
}: {
  item: DndParentEntry;
  columnId: DndColumn['id'];
  isOpen: (columnId: string, itemCode: string) => boolean;
  onToggleAccordion: (itemCode: string) => void;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const isVisible = useIsVisible(cardRef, scrollContainerRef);

  return (
    <div ref={cardRef}>
      {isVisible ? (
        <DndItemCard
          item={item}
          columnId={columnId}
          isOpen={isOpen}
          onToggleAccordion={onToggleAccordion}
        />
      ) : (
        <Box sx={{ height: 100 }} />
      )}
    </div>
  );
};

// ─── Zone de dépôt ────────────────────────────────────────────────────────────

export function Droppable(
  props: PropsWithChildren<{
    id: DndColumn['id'];
    columns: DndColumn[];
    isDropDisabled?: boolean;
  }>
) {
  const { isOver, setNodeRef } = useDroppable({
    id: props.id,
    disabled: props.isDropDisabled ?? false
  });
  const { isDark } = useSelectionContext();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        background: isOver ? (isDark ? '#2D3149' : '#ddddddff') : isDark ? '#1A1D2E' : '#f4f4f4',
        p: 2,
        minHeight: 0,
        borderRadius: 1,
        flexGrow: 1
      }}
      ref={setNodeRef}
    >
      {props.children}
    </Box>
  );
}

// ─── Overlay multi-sélection ──────────────────────────────────────────────────

export const MultiDragOverlay = ({
  selection,
  columns,
  isOpen
}: {
  selection: Set<string>;
  columns: DndColumn[];
  isOpen: (columnId: string, itemCode: string) => boolean;
}) => {
  const elements = Array.from(selection)
    .map((key) => {
      const [column, type, itemIdStr] = key.split('||');
      const itemId = Number(itemIdStr);
      const colItems = columns.find((col) => col.id === column)?.items;

      let parent: DndParentEntry | undefined;
      let child: DndParentEntry['children'][number] | undefined;

      if (type === 'parent') {
        parent = colItems?.find((item) => item.id === itemId);
      } else {
        for (const item of colItems ?? []) {
          const c = item.children.find((c) => c.id === itemId);
          if (c) {
            parent = item;
            child = c;
            break;
          }
        }
      }

      return { column, type, itemId, parent, child };
    })
    .sort((a, b) => a.parent?.label.trim().localeCompare(b.parent?.label.trim() || '') || 0);

  const ctx = useSelectionContext();

  return (
    <SelectionProvider value={{ ...ctx, isMultiDragActive: false, isDark: ctx.isDark }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {elements.map((el) =>
          el.type === 'parent' ? (
            <DndItemCard
              key={el.itemId}
              item={
                el.parent ?? {
                  id: el.itemId,
                  label: '',
                  children: [],
                  isFullParent: true
                }
              }
              columnId={el.column as DndColumn['id']}
              isOpen={isOpen}
              onToggleAccordion={() => null}
            />
          ) : (
            <DndChildCard
              key={el.itemId}
              child={el.child ?? { id: el.itemId, primaryLabel: '' }}
              parent={el.parent ?? { id: 0, label: '', children: [], isFullParent: false }}
              columnId={el.column as DndColumn['id']}
            />
          )
        )}
      </Box>
    </SelectionProvider>
  );
};

// ─── Hook de contexte DnD exposé pour les colonnes ───────────────────────────

export { useDndContext };
